import { useGuideEnabled } from "../lib/guide-preference";
import {
  ArrowRight,
  BookOpen,
  BookOpenCheck,
  CheckCircle2,
  Clock3,
  ExternalLink,
  GitMerge,
  GitPullRequest,
  Inbox,
  LayoutGrid,
  List,
  Loader,
  Search,
  Timer,
} from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { usePageMetadata } from "../hooks/usePageMetadata";
import {
  cachedOutstandingPulls,
  hostedAuthenticationRequired,
  loadHostedQueueSnapshot,
  loadOutstandingPullSnapshot,
  loadPersonalAnalytics,
  type HostedQueueSnapshot,
  type OutstandingPull,
  type OutstandingPullStatus,
  type PersonalAnalytics,
  type PersonalAnalyticsMetric,
  type PersonalAnalyticsRange,
} from "../lib/hosted";
import { handleLocalNavigation } from "../lib/navigation";
import { generalQueueDepth } from "../lib/queue-status";
import {
  absoluteLabel,
  authoredPullView,
  rankedForViewer,
  repositoryShortName,
  sinceLabel,
} from "../lib/pull-view";
import { startVisibilityPolling } from "../lib/visibility-polling";
import { AppShell } from "./AppShell";
import { PullBadge, PullBand, PullFlags, PullCard } from "./PullPresentation";
const MergePullDialog = lazy(() => import("./MergePullDialog").then((module) => ({ default: module.MergePullDialog })));

type WorkView = "board" | "list";

const columns: Array<{
  id: OutstandingPullStatus;
  title: string;
  description: string;
}> = [
  { id: "changes-requested", title: "Needs you", description: "Feedback is waiting" },
  { id: "ready", title: "Waiting for review", description: "With reviewers" },
  { id: "approved", title: "Approved", description: "Ready to land" },
  { id: "draft", title: "Drafts", description: "Still taking shape" },
];

function mentions(logins: string[] | undefined): string | undefined {
  return logins?.length ? logins.map((login) => `@${login}`).join(", ") : undefined;
}

export interface PullMoment {
  label: string;
  at?: string;
  logins?: string[];
}

/** The two facts that explain where a PR sits: when it opened, and what last happened to it. */
export function pullMoments(pull: OutstandingPull): { opened: PullMoment; decision: PullMoment } {
  const opened: PullMoment = { label: "Opened", at: pull.createdAt ?? pull.updatedAt };
  if (pull.status === "approved") {
    return { opened, decision: { label: "Approved", at: pull.approvedAt, logins: pull.approvedBy ?? pull.approvers } };
  }
  if (pull.status === "changes-requested") {
    // Pushing past a change request without asking the reviewer back parks the
    // PR where nothing routes it: CHEW holds it for the author, and GitHub
    // never puts it back in front of the reviewer.
    return pull.reReviewNeeded?.length
      ? { opened, decision: { label: "Re-request review", logins: pull.reReviewNeeded } }
      : { opened, decision: { label: "Changes requested", at: pull.changesRequestedAt, logins: pull.changesRequestedBy } };
  }
  if (pull.status === "draft") return { opened, decision: { label: "Not sent for review" } };
  return pull.reviewRequestedAt || pull.requestedReviewers.length > 0
    ? { opened, decision: { label: "Review requested", at: pull.reviewRequestedAt, logins: pull.requestedReviewers } }
    : { opened, decision: { label: "Awaiting review" } };
}

function Moment({ moment, tone }: { moment: PullMoment; tone?: string }) {
  const relative = sinceLabel(moment.at);
  const people = mentions(moment.logins);
  return (
    <span className={`home-pr-moment${tone ? ` is-${tone}` : ""}`} title={absoluteLabel(moment.at)}>
      <em>{moment.label}</em>
      {relative && <b>{relative === "just now" ? relative : `${relative} ago`}</b>}
      {people && <i>{people}</i>}
    </span>
  );
}

function metricValue(kind: keyof PersonalAnalytics["metrics"], value: number | null): string {
  if (value === null) return "—";
  if (kind === "medianResponseSeconds") {
    if (value < 3_600) return `${Math.max(1, Math.round(value / 60))}m`;
    if (value < 86_400) return `${(value / 3_600).toFixed(value < 36_000 ? 1 : 0)}h`;
    return `${(value / 86_400).toFixed(1)}d`;
  }
  if (kind === "reviewLoad") return Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value);
  return value.toLocaleString();
}

function metricDelta(metric: PersonalAnalyticsMetric, inverse = false): { detail: string; label: string; tone: string } | undefined {
  if (metric.value === null || metric.previousValue === null || metric.previousValue === 0) return undefined;
  const change = ((metric.value - metric.previousValue) / metric.previousValue) * 100;
  if (Math.abs(change) < 1) return { detail: "Even with the last period", label: "Even", tone: "neutral" };
  const positive = inverse ? change < 0 : change > 0;
  const label = `${change > 0 ? "+" : ""}${Math.round(change)}%`;
  return {
    detail: `${label} compared with the last period`,
    label,
    tone: positive ? "positive" : "quiet",
  };
}

function Sparkline({ values }: { values: number[] }) {
  const width = 112;
  const height = 34;
  const maximum = Math.max(1, ...values);
  const points = values.map((value, index) => {
    const x = values.length <= 1 ? 0 : (index / (values.length - 1)) * width;
    const y = height - (value / maximum) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg aria-hidden="true" className="home-sparkline" preserveAspectRatio="none" viewBox={`0 0 ${width} ${height}`}>
      <polyline fill="none" points={points} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

const guideLabels: Record<OutstandingPull["authoring"]["guideState"], string> = {
  missing: "Guide missing",
  generating: "Guide generating",
  draft: "Guide draft ready",
  current: "Guide current",
  stale: "Guide stale",
  failed: "Guide generation failed",
};

const guideIcons: Record<OutstandingPull["authoring"]["guideState"], typeof BookOpen> = {
  missing: BookOpen,
  generating: Loader,
  draft: BookOpen,
  current: BookOpenCheck,
  stale: BookOpen,
  failed: BookOpen,
};

function GuideMark({ state, editorUrl }: { state: OutstandingPull["authoring"]["guideState"]; editorUrl?: string }) {
  const enabled = useGuideEnabled();
  if (!enabled) return null;
  const Icon = guideIcons[state];
  if (editorUrl) {
    return (
      <a
        aria-label={`${guideLabels[state]} - open the guide editor`}
        className={`home-guide-mark state-${state}`}
        href={editorUrl}
        onClick={(event) => { event.stopPropagation(); handleLocalNavigation(event, editorUrl); }}
        title={`${guideLabels[state]} \u00b7 open the guide editor`}
      >
        <Icon aria-hidden="true" />
      </a>
    );
  }
  return (
    <span aria-label={guideLabels[state]} className={`home-guide-mark state-${state}`} role="img" title={guideLabels[state]}>
      <Icon aria-hidden="true" />
    </span>
  );
}

function MergeButton({ onMerge, subtle = false }: { onMerge: () => void; subtle?: boolean }) {
  return (
    <button className={`home-merge-button${subtle ? " is-subtle" : ""}`} onClick={onMerge} type="button">
      <GitMerge aria-hidden="true" />Merge
    </button>
  );
}

function AuthoredCard({ pull, onMerge }: { pull: OutstandingPull; onMerge: (pull: OutstandingPull) => void }) {
  const path = pull.authoring.url;
  const view = authoredPullView(pull);
  const moments = pullMoments(pull);
  return (
    <article className={`home-pr-card status-${pull.status}`}>
      <a className="home-pr-card-main" href={path} onClick={(event) => handleLocalNavigation(event, path)}>
        <strong>{pull.title}</strong>
        <PullBadge facts={view.badge} />
      </a>
      <div className="home-pr-card-foot">
        {view.holder && <span className="pv-holder" title={view.holder.detail}>@{view.holder.label}</span>}
        <PullFlags exceptions={view.exceptions} />
        <Moment moment={moments.decision} tone={pull.status} />
      </div>
      <div className="home-pr-card-actions">
        <GuideMark editorUrl={pull.authoring.editorUrl} state={pull.authoring.guideState} />
        {pull.status === "approved" && <MergeButton onMerge={() => onMerge(pull)} />}
        <a aria-label={`Open ${repositoryShortName(pull.repository)} pull request ${pull.pullNumber} on GitHub`} className="pv-github" href={pull.url} rel="noreferrer" target="_blank"><ExternalLink aria-hidden="true" /></a>
      </div>
    </article>
  );
}

export function MyWorkPage({
  onAuthenticationRequired,
  initialPulls,
  initialReviewSnapshot,
  local = false,
}: {
  onAuthenticationRequired: () => void;
  initialPulls?: OutstandingPull[];
  initialReviewSnapshot?: HostedQueueSnapshot;
  local?: boolean;
}) {
  usePageMetadata("Home · CHEW", "Your review work, authored pull requests, and personal review signals.");
  const [pulls, setPulls] = useState<OutstandingPull[] | undefined>(() => initialPulls ?? cachedOutstandingPulls());
  const [error, setError] = useState<unknown>();
  const [reload, setReload] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [reviewSnapshot, setReviewSnapshot] = useState<HostedQueueSnapshot | undefined>(initialReviewSnapshot);
  const [analytics, setAnalytics] = useState<PersonalAnalytics>();
  const [analyticsError, setAnalyticsError] = useState(false);
  const [range, setRange] = useState<PersonalAnalyticsRange>("30d");
  const [view, setView] = useState<WorkView>(() => localStorage.getItem("chew:work-view") === "list" ? "list" : "board");
  const [query, setQuery] = useState("");
  const [repository, setRepository] = useState("all");
  const [mergeTarget, setMergeTarget] = useState<OutstandingPull>();
  const [mergedNotice, setMergedNotice] = useState<string>();
  const [reviewQueueFailed, setReviewQueueFailed] = useState(false);

  useEffect(() => {
    if (!mergedNotice) return;
    const timer = window.setTimeout(() => setMergedNotice(undefined), 8_000);
    return () => window.clearTimeout(timer);
  }, [mergedNotice]);

  useEffect(() => {
    if (local) return;
    let cancelled = false;
    let pollTimer: number | undefined;
    setError(undefined);
    const load = async (refresh: boolean): Promise<void> => {
      try {
        const snapshot = await loadOutstandingPullSnapshot({ refresh });
        if (cancelled) return;
        setPulls(snapshot.items);
        setError(undefined);
        setRefreshing(snapshot.freshness.revalidating);
        if (snapshot.freshness.revalidating) pollTimer = window.setTimeout(() => { void load(true); }, 1_000);
      } catch (reason: unknown) {
        if (cancelled) return;
        setRefreshing(false);
        if (hostedAuthenticationRequired(reason)) onAuthenticationRequired();
        else setError(reason);
      }
    };
    let firstLoad = true;
    const stopPolling = startVisibilityPolling(async () => {
      await load(firstLoad ? reload > 0 : true);
      firstLoad = false;
    });
    return () => {
      cancelled = true;
      stopPolling();
      if (pollTimer !== undefined) window.clearTimeout(pollTimer);
    };
  }, [local, onAuthenticationRequired, reload]);

  // Note: the board deliberately does not subscribe to the notification
  // status feed. That feed derives PR status by different rules than
  // /api/my-pulls, and patching one field from it made cards flip between
  // columns every poll cycle.

  useEffect(() => {
    if (local) return;
    let cancelled = false;
    const stopPolling = startVisibilityPolling(() => loadHostedQueueSnapshot({ refresh: true })
      .then((snapshot) => {
        if (cancelled) return;
        setReviewSnapshot(snapshot);
        setReviewQueueFailed(false);
      })
      .catch(() => { if (!cancelled) setReviewQueueFailed(true); }));
    return () => { cancelled = true; stopPolling(); };
  }, [local]);

  useEffect(() => {
    let cancelled = false;
    setAnalyticsError(false);
    void loadPersonalAnalytics(range)
      .then((value) => { if (!cancelled) setAnalytics(value); })
      .catch((reason: unknown) => {
        if (cancelled) return;
        if (hostedAuthenticationRequired(reason)) onAuthenticationRequired();
        else setAnalyticsError(true);
      });
    return () => { cancelled = true; };
  }, [onAuthenticationRequired, range]);

  const next = useMemo(() => rankedForViewer(reviewSnapshot?.entries)[0], [reviewSnapshot]);
  const queueDepth = useMemo(() => reviewSnapshot && generalQueueDepth(reviewSnapshot.entries), [reviewSnapshot]);
  const queueDepthStale = Boolean(reviewSnapshot?.generation.stale);
  const repositories = useMemo(() => [...new Set((pulls ?? []).map((pull) => pull.repository))].sort(), [pulls]);
  const filtered = useMemo(() => (pulls ?? []).filter((pull) => {
    if (repository !== "all" && pull.repository !== repository) return false;
    const search = query.trim().toLocaleLowerCase();
    return !search || `${pull.title} ${pull.repository} ${pull.pullNumber}`.toLocaleLowerCase().includes(search);
  }).sort((left, right) => {
    // A NaN comparator makes the sort order depend on input permutation, so
    // cards would reshuffle between renders; keep a total order instead.
    const leftAt = Date.parse(left.updatedAt ?? "");
    const rightAt = Date.parse(right.updatedAt ?? "");
    return (Number.isFinite(rightAt) ? rightAt : 0) - (Number.isFinite(leftAt) ? leftAt : 0)
      || left.repository.localeCompare(right.repository)
      || left.pullNumber - right.pullNumber;
  }), [pulls, query, repository]);

  const changeView = (nextView: WorkView) => {
    setView(nextView);
    localStorage.setItem("chew:work-view", nextView);
  };

  const metricCards: Array<{
    key: keyof PersonalAnalytics["metrics"];
    label: string;
    detail: string;
    icon: typeof CheckCircle2;
    values: number[];
    inverse?: boolean;
  }> = [
    { key: "reviewsCompleted", label: "Reviews completed", detail: "Published GitHub reviews", icon: CheckCircle2, values: analytics?.trend.map((item) => item.reviewsCompleted) ?? [] },
    { key: "approvalsReceived", label: "Approvals received", detail: "On your authored PRs", icon: GitPullRequest, values: analytics?.trend.map((item) => item.approvalsReceived) ?? [] },
    { key: "reviewLoad", label: "Review load", detail: "LOC you helped review", icon: Timer, values: analytics?.trend.map((item) => item.reviewLoad) ?? [] },
    { key: "medianResponseSeconds", label: "Median response", detail: "Direct request to first review", icon: Clock3, values: analytics?.trend.map((item) => item.medianResponseSeconds ?? 0) ?? [], inverse: true },
  ];

  return (
    <AppShell active="home" queueNext={next} queueSnapshot={reviewSnapshot}>
      <div className="home-content">
        <section className="home-hero">
          <div className="home-greeting">
            <span className="home-eyebrow">Your workspace</span>
            <h1>Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}.</h1>
            <p>Your reviews and authored work, without the noise.</p>
          </div>
        </section>

        <div className="home-overview">
          <section className={`home-next-review${next ? " has-review" : ""}`}>
            <div className="home-next-icon"><CheckCircle2 aria-hidden="true" /></div>
            <div className="home-next-copy">
              <span>Your review queue</span>
              {next ? <><strong>{next.title}</strong><small>{repositoryShortName(next.repository)} · #{next.pullNumber} · {next.forMe.detail}</small></>
                : !reviewSnapshot ? (
                  reviewQueueFailed
                    ? <><strong>Queue unavailable</strong><small>CHEW could not reach your review queue. It keeps retrying.</small></>
                    : <><strong>Checking your queue…</strong><small>Loading the best next review for you.</small></>
                )
                : <><strong>You’re all caught up</strong><small>No useful review currently routes to you.</small></>}
            </div>
            <a
              aria-label={queueDepth === undefined
                ? "Global queue depth is loading"
                : `${queueDepth} pull ${queueDepth === 1 ? "request is" : "requests are"} waiting for anyone in the global queue${queueDepthStale ? " (last known)" : ""}`}
              className={`home-queue-depth${queueDepthStale ? " is-stale" : ""}`}
              href="/operations/queue"
              onClick={(event) => handleLocalNavigation(event, "/operations/queue")}
              title="Open PRs ready for review that GitHub routes to nobody in particular. Excludes requested reviewers, teams, and assignees."
            >
              <strong>{queueDepth ?? "–"}</strong>
              <span><em>Global queue</em><small>{queueDepthStale ? "Last known depth" : "Waiting for anyone"}</small></span>
            </a>
            <a className={next ? "primary-button" : "secondary-button"} href="/queue" onClick={(event) => handleLocalNavigation(event, "/queue")}>
              {next ? ["claimed-by-self", "in-review-by-self"].includes(next.forMe.outcome) ? "Resume in queue" : "Open queue" : "View queue"}<ArrowRight aria-hidden="true" />
            </a>
          </section>

          <section aria-label="Personal review analytics" className="home-insights">
            <div className="home-range" aria-label="Analytics period">
              {(["7d", "30d", "90d"] as PersonalAnalyticsRange[]).map((value) => (
                <button aria-pressed={range === value} key={value} onClick={() => setRange(value)} type="button">{value}</button>
              ))}
            </div>
            <div className="home-metrics">
              {metricCards.map((card) => {
                const metric = analytics?.metrics[card.key];
                const delta = metric && metricDelta(metric, card.inverse);
                const Icon = card.icon;
                return (
                  <article key={card.key} title={card.detail}>
                    <header><span><Icon aria-hidden="true" />{card.label}</span>{delta && <em aria-label={delta.detail} className={`is-${delta.tone}`} title={delta.detail}>{delta.label}</em>}</header>
                    <div className="home-metric-value"><strong>{metricValue(card.key, metric?.value ?? null)}</strong><Sparkline values={card.values} /></div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
        {analyticsError && <p className="home-analytics-note">Personal signals are temporarily unavailable. Your queue and PRs are unaffected.</p>}
        {mergedNotice && <p aria-live="polite" className="home-analytics-note" role="status">{mergedNotice}</p>}

        <section className="home-work-section">
          <header className="home-work-header">
            <div><span>Authored by you</span><h2>Your pull requests</h2><p>{pulls ? `${pulls.length} open across ${repositories.length} ${repositories.length === 1 ? "repository" : "repositories"}` : "Loading open pull requests…"}{refreshing ? " · refreshing" : ""}</p></div>
            <div className="home-work-controls">
              <label className="home-search"><Search aria-hidden="true" /><input aria-label="Search pull requests" onChange={(event) => setQuery(event.target.value)} placeholder="Search PRs" value={query} /></label>
              <select aria-label="Filter by repository" onChange={(event) => setRepository(event.target.value)} value={repository}>
                <option value="all">All repositories</option>
                {repositories.map((value) => <option key={value} value={value}>{repositoryShortName(value)}</option>)}
              </select>
              <div className="home-view-toggle" role="group" aria-label="Pull request view">
                <button aria-pressed={view === "board"} onClick={() => changeView("board")} type="button"><LayoutGrid aria-hidden="true" /><span>Board</span></button>
                <button aria-pressed={view === "list"} onClick={() => changeView("list")} type="button"><List aria-hidden="true" /><span>List</span></button>
              </div>
            </div>
          </header>

          {!pulls && !error && <div className="home-loading" role="status">Loading your pull requests…</div>}
          {Boolean(error) && !pulls && <div className="home-empty"><Inbox aria-hidden="true" /><strong>CHEW could not load your pull requests.</strong><button onClick={() => setReload((value) => value + 1)} type="button">Try again</button></div>}
          {Boolean(error) && pulls && <p className="home-analytics-note" role="status">Background refresh failed; showing your last loaded pull requests.</p>}
          {pulls && filtered.length === 0 && <div className="home-empty"><Inbox aria-hidden="true" /><strong>{pulls.length === 0 ? "No open pull requests." : "No pull requests match those filters."}</strong></div>}

          {filtered.length > 0 && view === "board" && (
            <div className="home-board">
              {columns.map((column) => {
                const items = filtered.filter((pull) => pull.status === column.id);
                return (
                  <section className={`home-board-column column-${column.id}`} key={column.id}>
                    <header><div><i /><strong>{column.title}</strong><small>{column.description}</small></div><span>{items.length}</span></header>
                    <div className="home-board-list">
                      {items.map((pull) => <AuthoredCard key={`${pull.repository}#${pull.pullNumber}`} onMerge={setMergeTarget} pull={pull} />)}
                      {items.length === 0 && <p>Nothing here</p>}
                    </div>
                  </section>
                );
              })}
            </div>
          )}

          {filtered.length > 0 && view === "list" && (
            <div className="home-pr-grouped-list">
              {columns.map((column) => {
                const items = filtered.filter((pull) => pull.status === column.id);
                if (items.length === 0) return null;
                return (
                  <PullBand blurb={column.description} count={items.length} key={column.id} title={column.title}>
                    {items.map((pull) => {
                      const view = authoredPullView(pull);
                      const moments = pullMoments(pull);
                      return (
                        <PullCard
                          actions={<>
                            <GuideMark editorUrl={pull.authoring.editorUrl} state={pull.authoring.guideState} />
                            {pull.status === "approved" && <MergeButton onMerge={() => setMergeTarget(pull)} subtle />}
                          </>}
                          key={`${pull.repository}#${pull.pullNumber}`}
                          status={<Moment moment={moments.decision} tone={pull.status} />}
                          view={view}
                        />
                      );
                    })}
                  </PullBand>
                );
              })}
            </div>
          )}
        </section>
      </div>
      {mergeTarget && (
        <Suspense fallback={
          <div className="merge-backdrop" role="presentation">
            <section aria-busy="true" className="merge-dialog"><p className="merge-dialog-loading" role="status">Opening merge…</p></section>
          </div>
        }><MergePullDialog
          onClose={() => setMergeTarget(undefined)}
          onMerged={(sha) => {
            const merged = mergeTarget;
            setMergeTarget(undefined);
            setMergedNotice(`Merged ${repositoryShortName(merged.repository)} #${merged.pullNumber} as ${sha.slice(0, 7)}.`);
            setPulls((current) => current?.filter((pull) =>
              pull.repository !== merged.repository || pull.pullNumber !== merged.pullNumber));
            setReload((value) => value + 1);
            // The review queue card polls on its own 30s cadence; refresh it
            // now so the merged PR does not linger as someone's next review.
            void loadHostedQueueSnapshot({ refresh: true })
              .then((snapshot) => setReviewSnapshot(snapshot))
              .catch(() => undefined);
          }}
          pull={mergeTarget}
        /></Suspense>
      )}
    </AppShell>
  );
}
